import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Lock, Unlock, ChevronUp, ChevronDown } from 'lucide-react';
import { Gate } from '../../AccessStudio';
import GradeEntrySheet from './GradeEntrySheet';
import CompetencySheet from './CompetencySheet';
import useScopedClasses from '../useScopedClasses';

// Grading — mirrors the school's model:
//   Year → Semesters (weight % of general average)
//        → Exams (global, weight % within semester, opened/closed here)
//   Subjects per class: coefficient + max grade. Competencies attach to
//   any subject as checkmarks (never calculated).
const GradingView = ({ apiCall, year, showSuccess, showError }) => {
  const [mode, setMode] = useState('entry'); // entry | competency | setup
  const { visibleClasses, className, setClassName } = useScopedClasses(apiCall, year);
  const [subjects, setSubjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [exams, setExams] = useState([]);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newMap, setNewMap] = useState({ subjectId: '', coefficient: 1, maxGrade: 20, parentId: '' });
  const [newExam, setNewExam] = useState({ periodId: '', name: '', weight: 50 });

  useEffect(() => {
    apiCall('/academic/grading/subjects').then(setSubjects).catch(() => {});
  }, [apiCall]);

  const loadPeriods = useCallback(async () => {
    if (!className) return;
    try {
      const p = await apiCall(`/academic/grading/periods?academicYear=${year}&className=${encodeURIComponent(className)}`);
      setPeriods(p);
      // periods are class-bound now — re-anchor the add-exam picker
      setNewExam(f => ({ ...f, periodId: p.some(x => String(x.PeriodID) === String(f.periodId)) ? f.periodId : (p[0]?.PeriodID || '') }));
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, showError]);

  const loadExams = useCallback(async () => {
    if (!className) return;
    try { setExams(await apiCall(`/academic/grading/exams?academicYear=${year}&className=${encodeURIComponent(className)}`)); }
    catch (err) { showError(err.message); }
  }, [apiCall, year, className, showError]);

  useEffect(() => { loadPeriods(); loadExams(); }, [loadPeriods, loadExams]);

  const loadMappings = useCallback(async () => {
    if (!className) return;
    try {
      setMappings(await apiCall(`/academic/grading/class-subjects?academicYear=${year}&className=${encodeURIComponent(className)}`));
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, showError]);

  useEffect(() => { loadMappings(); }, [loadMappings]);

  // ---- setup handlers ----
  const savePeriod = async (p) => {
    try {
      await apiCall(`/academic/grading/periods/${p.PeriodID}`, {
        method: 'PUT', body: JSON.stringify({ periodName: p.PeriodName, weightPct: parseFloat(p.WeightPct) || 0 })
      });
      showSuccess('Period saved');
      loadPeriods();
    } catch (err) { showError(err.message); }
  };

  const addExam = async () => {
    if (!newExam.name.trim() || !newExam.periodId) { showError('Exam name and semester are required'); return; }
    try {
      await apiCall('/academic/grading/exams', {
        method: 'POST',
        body: JSON.stringify({
          academicYear: year, className, periodId: parseInt(newExam.periodId),
          examName: newExam.name, weightPct: parseFloat(newExam.weight) || 50,
          sortOrder: exams.filter(e => e.PeriodID === parseInt(newExam.periodId)).length + 1
        })
      });
      showSuccess('Exam created (closed — open it when entry should start)');
      setNewExam(f => ({ ...f, name: '', weight: 50 }));
      loadExams();
    } catch (err) { showError(err.message); }
  };

  const saveExam = async (e) => {
    try {
      await apiCall(`/academic/grading/exams/${e.ExamID}`, {
        method: 'PUT', body: JSON.stringify({ examName: e.ExamName, weightPct: parseFloat(e.WeightPct) || 0 })
      });
      showSuccess('Exam saved');
      loadExams();
    } catch (err) { showError(err.message); }
  };

  const toggleExam = async (e) => {
    try {
      await apiCall(`/academic/grading/exams/${e.ExamID}`, {
        method: 'PUT', body: JSON.stringify({ isOpen: !e.IsOpen })
      });
      showSuccess(`${e.ExamName} ${e.IsOpen ? 'closed' : 'opened'} for grade entry`);
      loadExams();
    } catch (err) { showError(err.message); }
  };

  const deleteExam = async (e) => {
    if (!window.confirm(e.IsDirect ? 'Remove the direct mark (so you can add exams instead)?' : `Delete ${e.ExamName}?`)) return;
    try {
      await apiCall(`/academic/grading/exams/${e.ExamID}`, { method: 'DELETE' });
      showSuccess(e.IsDirect ? 'Direct mark removed — add exams to this period' : 'Exam deleted');
      loadExams();
    } catch (err) { showError(err.message); }
  };

  // One mark per student, no exam breakdown (e.g. a trimestre)
  const makeDirect = async (p) => {
    try {
      const r = await apiCall(`/academic/grading/periods/${p.PeriodID}/direct`, { method: 'POST' });
      showSuccess(r.message);
      loadExams();
    } catch (err) { showError(err.message); }
  };

  const copyStructure = async () => {
    if (!window.confirm(`Copy ${className}'s exam structure to ALL other classes? Existing exams are kept, missing ones are created.`)) return;
    try {
      const r = await apiCall('/academic/grading/exams/copy', {
        method: 'POST',
        body: JSON.stringify({ academicYear: year, fromClassName: className, toAll: true })
      });
      showSuccess(r.message);
    } catch (err) { showError(err.message); }
  };

  const addPeriod = async () => {
    const name = window.prompt('New period name (e.g. Semester 1):');
    if (!name?.trim()) return;
    try {
      await apiCall('/academic/grading/periods', {
        method: 'POST',
        body: JSON.stringify({ academicYear: year, className, periodName: name.trim(), weightPct: 0, sortOrder: periods.length + 1 })
      });
      showSuccess('Period created — set its weight %');
      loadPeriods();
    } catch (err) { showError(err.message); }
  };

  const deletePeriod = async (p) => {
    if (!window.confirm(`Delete ${p.PeriodName}? Only possible when it has no exams or competency marks.`)) return;
    try {
      await apiCall(`/academic/grading/periods/${p.PeriodID}`, { method: 'DELETE' });
      showSuccess('Period deleted');
      loadPeriods(); loadExams();
    } catch (err) { showError(err.message); }
  };

  const addSubject = async () => {
    if (!newSubjectName.trim()) return;
    try {
      await apiCall('/academic/grading/subjects', { method: 'POST', body: JSON.stringify({ subjectName: newSubjectName }) });
      showSuccess('Subject created');
      setNewSubjectName('');
      setSubjects(await apiCall('/academic/grading/subjects'));
    } catch (err) { showError(err.message); }
  };

  const addMapping = async () => {
    if (!newMap.subjectId) { showError('Pick a subject'); return; }
    try {
      await apiCall('/academic/grading/class-subjects', {
        method: 'POST',
        body: JSON.stringify({ academicYear: year, className, subjectId: parseInt(newMap.subjectId),
          coefficient: parseFloat(newMap.coefficient) || 1, maxGrade: parseFloat(newMap.maxGrade) || 20,
          parentClassSubjectId: newMap.parentId ? parseInt(newMap.parentId) : null })
      });
      showSuccess(newMap.parentId ? 'Domain added' : `Subject assigned to ${className}`);
      setNewMap(f => ({ subjectId: '', coefficient: 1, maxGrade: 20, parentId: f.parentId }));
      loadMappings();
    } catch (err) { showError(err.message); }
  };

  const saveMapping = async (m) => {
    try {
      await apiCall(`/academic/grading/class-subjects/${m.ClassSubjectID}`, {
        method: 'PUT',
        body: JSON.stringify({ coefficient: parseFloat(m.Coefficient) || 1, maxGrade: parseFloat(m.MaxGrade) || 20 })
      });
      showSuccess('Subject settings saved');
      loadMappings();
    } catch (err) { showError(err.message); }
  };

  const deleteMapping = async (m) => {
    if (!window.confirm(`Remove ${m.SubjectName} from ${className}?`)) return;
    try {
      await apiCall(`/academic/grading/class-subjects/${m.ClassSubjectID}`, { method: 'DELETE' });
      showSuccess('Subject removed');
      loadMappings();
    } catch (err) { showError(err.message); }
  };

  // Swap a subject with its neighbour among its siblings (top-level of
  // the class, or domains of the same parent). Bulletin prints this order.
  const moveMapping = async (m, dir) => {
    const parentKey = m.ParentClassSubjectID || null;
    const siblings = mappings.filter(x => (x.ParentClassSubjectID || null) === parentKey);
    const idx = siblings.findIndex(x => x.ClassSubjectID === m.ClassSubjectID);
    const j = idx + dir;
    if (j < 0 || j >= siblings.length) return;
    const ids = siblings.map(x => x.ClassSubjectID);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try {
      await apiCall('/academic/grading/class-subjects/reorder', {
        method: 'POST', body: JSON.stringify({ orderedIds: ids })
      });
      loadMappings();
    } catch (err) { showError(err.message); }
  };

  const setMapField = (id, field, value) =>
    setMappings(ms => ms.map(m => m.ClassSubjectID === id ? { ...m, [field]: value } : m));
  const setPeriodField = (id, field, value) =>
    setPeriods(ps => ps.map(p => p.PeriodID === id ? { ...p, [field]: value } : p));
  const setExamField = (id, field, value) =>
    setExams(es => es.map(e => e.ExamID === id ? { ...e, [field]: value } : e));

  const periodWeightTotal = periods.reduce((s, p) => s + Number(p.WeightPct || 0), 0);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {(() => {
            const modeBtn = (id, label) => (
              <button onClick={() => setMode(id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${mode === id ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
                {label}
              </button>
            );
            return (
              <>
                <Gate permission="academic.grading.enter" label="Grade Entry Tab">
                  {modeBtn('entry', 'Grade Entry')}
                </Gate>
                {/* Teachers mark competencies for their own students */}
                <Gate permission="academic.grading.enter" label="Competencies Tab">
                  {modeBtn('competency', 'Competencies')}
                </Gate>
                <Gate adminOnly label="Exams Setup Tab">
                  {modeBtn('setup', 'Exams Setup')}
                </Gate>
              </>
            );
          })()}
        </div>
        <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
        </select>
      </div>

      {mode === 'setup' && (
        <div className="space-y-4">
          {/* The tree: semesters and their exams */}
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex flex-wrap justify-between items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-800">Exam Structure — {className} · {year}</h3>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full ${Math.abs(periodWeightTotal - 100) < 0.01 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  Period weights: {periodWeightTotal}%{Math.abs(periodWeightTotal - 100) >= 0.01 && ' (should be 100%)'}
                </span>
                <Gate permission="academic.grading.configure" label="Add Grading Period">
                  <button onClick={addPeriod} className="text-xs px-2 py-1 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50">
                    + Period
                  </button>
                </Gate>
                <Gate permission="academic.grading.configure" label="Copy Exam Structure">
                  <button onClick={copyStructure} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50">
                    Copy to all classes
                  </button>
                </Gate>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              General average = periods weighted by their %. Each period = its exams weighted by their %.
              Exams belong to <b>{className}</b> — build one class then "Copy to all classes".
              Open an exam to allow grade entry; close it when done.
            </p>

            <div className="space-y-4">
              {periods.map(p => {
                const pExams = exams.filter(e => e.PeriodID === p.PeriodID);
                const examTotal = pExams.reduce((s, e) => s + Number(e.WeightPct || 0), 0);
                return (
                  <div key={p.PeriodID} className="border border-gray-200 rounded-lg">
                    {/* Semester row */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-t-lg">
                      <input type="text" value={p.PeriodName}
                        onChange={e => setPeriodField(p.PeriodID, 'PeriodName', e.target.value)}
                        className="border rounded px-2 py-1 text-sm font-semibold w-40" />
                      <input type="number" value={p.WeightPct}
                        onChange={e => setPeriodField(p.PeriodID, 'WeightPct', e.target.value)}
                        className="w-16 border rounded px-2 py-1 text-sm text-right" />
                      <span className="text-xs text-gray-500">% of general average</span>
                      <Gate permission="academic.grading.configure" label="Edit Grading Period">
                        <button onClick={() => savePeriod(p)} className="text-blue-600 text-xs hover:underline">Save</button>
                      </Gate>
                      <Gate permission="academic.grading.configure" label="Delete Grading Period">
                        <button onClick={() => deletePeriod(p)} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </Gate>
                      <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${Math.abs(examTotal - 100) < 0.01 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        exams: {examTotal}%
                      </span>
                    </div>
                    {/* Exams of this semester — or its single direct mark */}
                    <div className="p-2 space-y-1">
                      {pExams.length === 1 && pExams[0].IsDirect ? (
                        <div className="flex items-center gap-2 pl-6 pr-2 py-1 text-sm">
                          <span className="text-gray-300">└</span>
                          <span className="text-gray-700">Graded directly — one mark per student</span>
                          <Gate permission="academic.grading.configure" label="Open/Close Exam">
                            <button onClick={() => toggleExam(pExams[0])}
                              className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${pExams[0].IsOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {pExams[0].IsOpen ? <><Unlock size={11} /> open</> : <><Lock size={11} /> closed</>}
                            </button>
                          </Gate>
                          <Gate permission="academic.grading.configure" label="Delete Exam">
                            <button onClick={() => deleteExam(pExams[0])} className="text-red-400 hover:text-red-600"
                              title="Remove the direct mark to split into exams"><Trash2 size={13} /></button>
                          </Gate>
                        </div>
                      ) : pExams.map(e => (
                        <div key={e.ExamID} className="flex items-center gap-2 pl-6 pr-2 py-1 text-sm">
                          <span className="text-gray-300">└</span>
                          <input type="text" value={e.ExamName}
                            onChange={ev => setExamField(e.ExamID, 'ExamName', ev.target.value)}
                            className="border rounded px-2 py-1 text-sm w-40" />
                          <input type="number" value={e.WeightPct}
                            onChange={ev => setExamField(e.ExamID, 'WeightPct', ev.target.value)}
                            className="w-16 border rounded px-2 py-1 text-sm text-right" />
                          <span className="text-xs text-gray-500">% of {p.PeriodName}</span>
                          <Gate permission="academic.grading.configure" label="Edit Exam">
                            <button onClick={() => saveExam(e)} className="text-blue-600 text-xs hover:underline">Save</button>
                          </Gate>
                          <Gate permission="academic.grading.configure" label="Open/Close Exam">
                            <button onClick={() => toggleExam(e)}
                              className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${e.IsOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {e.IsOpen ? <><Unlock size={11} /> open</> : <><Lock size={11} /> closed</>}
                            </button>
                          </Gate>
                          <Gate permission="academic.grading.configure" label="Delete Exam">
                            <button onClick={() => deleteExam(e)} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                          </Gate>
                        </div>
                      ))}
                      {pExams.length === 0 && (
                        <div className="flex items-center gap-3 pl-8 py-1">
                          <p className="text-xs text-gray-400 italic">no exams yet</p>
                          <Gate permission="academic.grading.configure" label="Grade Period Directly">
                            <button onClick={() => makeDirect(p)}
                              className="text-xs px-2 py-0.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50">
                              Grade directly (one mark)
                            </button>
                          </Gate>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add exam */}
            <Gate permission="academic.grading.configure" label="Add Exam">
              <div className="flex flex-wrap items-end gap-2 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Semester</label>
                  <select value={newExam.periodId} onChange={e => setNewExam(f => ({ ...f, periodId: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm">
                    {periods.map(p => <option key={p.PeriodID} value={p.PeriodID}>{p.PeriodName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Exam name</label>
                  <input type="text" value={newExam.name} onChange={e => setNewExam(f => ({ ...f, name: e.target.value }))}
                    placeholder="Exam 1" className="border rounded-lg px-2 py-1.5 text-sm w-36" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Weight %</label>
                  <input type="number" value={newExam.weight} onChange={e => setNewExam(f => ({ ...f, weight: e.target.value }))}
                    className="w-20 border rounded-lg px-2 py-1.5 text-sm text-right" />
                </div>
                <button onClick={addExam} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1">
                  <Plus size={14} /> Add Exam
                </button>
              </div>
            </Gate>
          </div>

          {/* Subjects per class */}
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-semibold text-gray-800">Subjects</h3>
              <select value={className} onChange={e => setClassName(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
                {visibleClasses.map(cl => <option key={cl.ClassName} value={cl.ClassName}>{cl.ClassName}</option>)}
              </select>
            </div>

            <Gate permission="academic.grading.configure" label="Assign Subject to Class">
              <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Subject</label>
                  <select value={newMap.subjectId} onChange={e => setNewMap(f => ({ ...f, subjectId: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm">
                    <option value="">— subject —</option>
                    {subjects.filter(s => s.IsActive).map(s => <option key={s.SubjectID} value={s.SubjectID}>{s.SubjectName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Domain of</label>
                  <select value={newMap.parentId} onChange={e => setNewMap(f => ({ ...f, parentId: e.target.value }))}
                    className="border rounded-lg px-2 py-1.5 text-sm">
                    <option value="">— top-level subject —</option>
                    {mappings.filter(m => !m.ParentClassSubjectID).map(m => (
                      <option key={m.ClassSubjectID} value={m.ClassSubjectID}>{m.SubjectName}</option>
                    ))}
                  </select>
                </div>
                {!newMap.parentId && (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Coef</label>
                    <input type="number" step="0.5" value={newMap.coefficient}
                      onChange={e => setNewMap(f => ({ ...f, coefficient: e.target.value }))}
                      className="w-16 border rounded-lg px-2 py-1.5 text-sm text-right" />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Out of</label>
                  <input type="number" value={newMap.maxGrade}
                    onChange={e => setNewMap(f => ({ ...f, maxGrade: e.target.value }))}
                    className="w-16 border rounded-lg px-2 py-1.5 text-sm text-right" />
                </div>
                <button onClick={addMapping} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1">
                  <Plus size={14} /> {newMap.parentId ? 'Add Domain' : 'Assign'}
                </button>
                <div className="flex items-end gap-2 ml-auto">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">New subject (catalog)</label>
                    <input type="text" value={newSubjectName} onChange={e => setNewSubjectName(e.target.value)}
                      placeholder="e.g. Mathématiques" className="border rounded-lg px-2 py-1.5 text-sm" />
                  </div>
                  <button onClick={addSubject} className="px-3 py-1.5 border border-blue-300 text-blue-700 text-sm rounded-lg hover:bg-blue-50">
                    Create
                  </button>
                </div>
              </div>
            </Gate>

            <div className="space-y-1.5">
              {mappings.filter(m => !m.ParentClassSubjectID).map(parent => {
                const children = mappings.filter(c => c.ParentClassSubjectID === parent.ClassSubjectID);
                const renderRow = (m, isChild) => (
                  <div key={m.ClassSubjectID}
                       className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isChild ? 'ml-8 bg-white border border-gray-200' : 'bg-gray-50'}`}>
                    {isChild && <span className="text-gray-300">└</span>}
                    <span className={`flex-1 ${isChild ? 'text-gray-700' : 'font-medium text-gray-800'}`}>
                      {m.SubjectName}
                      {!isChild && children.length > 0 && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {children.length} domains
                        </span>
                      )}
                      {m.CompetencyCount > 0 && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          {m.CompetencyCount} competencies
                        </span>
                      )}
                    </span>
                    {!isChild && (
                      <>
                        <label className="text-xs text-gray-500">coef</label>
                        <input type="number" step="0.5" value={m.Coefficient}
                          onChange={e => setMapField(m.ClassSubjectID, 'Coefficient', e.target.value)}
                          className="w-14 border rounded px-1 py-1 text-xs text-right" />
                      </>
                    )}
                    <label className="text-xs text-gray-500">out of</label>
                    <input type="number" value={m.MaxGrade}
                      disabled={!isChild && children.length > 0}
                      title={!isChild && children.length > 0 ? 'Auto — sum of its domains' : undefined}
                      onChange={e => setMapField(m.ClassSubjectID, 'MaxGrade', e.target.value)}
                      className={`w-14 border rounded px-1 py-1 text-xs text-right ${!isChild && children.length > 0 ? 'bg-gray-100 text-gray-500' : ''}`} />
                    <Gate permission="academic.grading.configure" label="Reorder Subjects">
                      <span className="flex flex-col">
                        <button onClick={() => moveMapping(m, -1)} className="text-gray-400 hover:text-blue-600" title="Move up"><ChevronUp size={13} /></button>
                        <button onClick={() => moveMapping(m, 1)} className="text-gray-400 hover:text-blue-600" title="Move down"><ChevronDown size={13} /></button>
                      </span>
                    </Gate>
                    <Gate permission="academic.grading.configure" label="Edit Class Subject">
                      <button onClick={() => saveMapping(m)} className="text-blue-600 text-xs hover:underline">Save</button>
                    </Gate>
                    <Gate permission="academic.grading.configure" label="Remove Class Subject">
                      <button onClick={() => deleteMapping(m)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    </Gate>
                  </div>
                );
                return (
                  <React.Fragment key={parent.ClassSubjectID}>
                    {renderRow(parent, false)}
                    {children.map(c => renderRow(c, true))}
                  </React.Fragment>
                );
              })}
              {mappings.length === 0 && (
                <p className="text-sm text-gray-500 italic text-center py-3">No subjects assigned to {className} yet.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {mode === 'entry' && (
        <GradeEntrySheet
          apiCall={apiCall} year={year} className={className}
          mappings={mappings} periods={periods}
          showSuccess={showSuccess} showError={showError}
        />
      )}

      {mode === 'competency' && (
        <CompetencySheet
          apiCall={apiCall} year={year} className={className}
          mappings={mappings} periods={periods}
          showSuccess={showSuccess} showError={showError}
        />
      )}
    </div>
  );
};

export default GradingView;
